import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, AlertTriangle, Frown, Info, Loader2, Zap, ExternalLink } from 'lucide-react';
import { trackCalculatorStart, trackCalculatorResult } from '../utils/analytics';
import { SARASOTA, countyForZip } from '../data/sarasotaCalculator';

/*
 * Temporary Sarasota Housing Authority mockup of the public calculator.
 * Intentionally unlinked. Same income-limit API as /calculator, but:
 *   - HCV only (SHA converted its public housing in Aug 2026)
 *   - ZIP must be on SHA's payment-standard list
 *   - adds voucher size, unit type and tenant-paid utilities
 *   - subtracts SHA's 2026 utility allowance from TTP and shows the
 *     payment standard for the ZIP
 * The general /calculator page is untouched.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'https://housing-external.onrender.com';

const DEDUCTIONS = {
  dependentDeduction: 480,
  elderlyDisabledDeduction: 525,
  medicalThresholdPct: 0.03,
  ttpPctAdjusted: 0.30,
  ttpPctGross: 0.10,
  moveInShareCapPct: 0.40,
};

const BRAND = SARASOTA.themeColor;

// SHA publishes five HUD-52667 building types. SHA staff use the SINGLE FAMILY
// schedule when they estimate for a family (Yani Rosado, 2026-10-19), so that is
// the default; residents who know their building type can change it.
const DEFAULT_UNIT_TYPE = 'SINGLE FAMILY';
const UNIT_TYPE_LABELS = {
  'SINGLE FAMILY': 'Single-family house',
  'DUPLEX': 'Duplex',
  'LOW RISE/TOWNHOUSE/ROWHOUSE': 'Townhouse, rowhouse or low-rise (1–2 floors)',
  'HIGH RISE/MULTI-FAMILY/MULTI-STORY': 'Apartment building (3+ floors)',
  'MOBILE HOME': 'Mobile home',
};
const UNIT_TYPE_ORDER = Object.keys(UNIT_TYPE_LABELS);

// Voucher sizes SHA issues (Admin Plan 5-II.B: a single person gets 1 bedroom).
const VOUCHER_SIZES = [1, 2, 3, 4, 5];

const HEAT_FUELS = ['Electric', 'Electric - Heat Pump', 'Natural Gas', 'Bottled Gas'];
const COOK_FUELS = ['Electric', 'Natural Gas', 'Bottled Gas'];

const DEFAULT_UTILS = {
  electric: true,            // lights, A/C, appliances
  heating: 'Electric',       // fuel, or 'landlord'
  cooking: 'Electric',
  waterHeating: 'Electric',
  water: false,
  sewer: false,
  trash: false,
  range: false,
  refrigerator: false,
};

// "Utilities included": the landlord pays everything, so no allowance applies.
const ALL_INCLUDED_UTILS = {
  electric: false,
  heating: 'landlord',
  cooking: 'landlord',
  waterHeating: 'landlord',
  water: false,
  sewer: false,
  trash: false,
  range: false,
  refrigerator: false,
};

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtMonthly = fmt;

function incomeBracket(income) {
  if (income < 20000) return 'under_20k';
  if (income < 40000) return '20k_40k';
  if (income < 60000) return '40k_60k';
  if (income < 80000) return '60k_80k';
  return '80k_plus';
}

// SHA subsidy standards (Admin Plan 5-II.B): a single person gets 1 bedroom,
// head and spouse share, other members share a room until age 12. The chart's
// smallest voucher for each household size is 1 BR for 1-2 people, 2 BR for
// 3-4, 3 BR for 5-6, 4 BR for 7-8. Composition can push it higher (a parent
// and one child is 2 BR), so the resident can change it.
function defaultVoucherSize(householdSize) {
  if (householdSize <= 2) return 1;
  return Math.min(5, Math.ceil(householdSize / 2));
}

// 24 CFR 982.505(c) and 982.517(d), SHA Admin Plan 6-III.C: the payment
// standard and the utility allowance both use the LOWER of the voucher size
// and the bedrooms in the unit actually rented. One person in a 4-bedroom
// house is figured on the 1-bedroom schedule.
function sizeUsed(voucherSize, unitBedrooms) {
  if (unitBedrooms == null) return voucherSize;
  return Math.min(voucherSize, unitBedrooms);
}

function computeUA(county, unitType, br, u) {
  const s = SARASOTA.ua.schedules[county]?.[unitType];
  if (!s) return { lines: [], total: 0 };
  const lines = [];
  const add = (label, key) => {
    const row = s[key];
    if (!row) return;
    lines.push({ label, amount: row[br] || 0 });
  };
  if (u.heating !== 'landlord') add(`Heating (${u.heating.toLowerCase()})`, `Heating - ${u.heating}`);
  if (u.cooking !== 'landlord') add(`Cooking (${u.cooking.toLowerCase()})`, `Cooking - ${u.cooking}`);
  if (u.electric) {
    add('Other electric (lights, appliances)', 'Other Electric');
    add('Air conditioning', 'Air Conditioning');
  }
  if (u.waterHeating !== 'landlord') add(`Water heating (${u.waterHeating.toLowerCase()})`, `Water Heating - ${u.waterHeating}`);
  if (u.water) add('Water', 'Water');
  if (u.sewer) add('Sewer', 'Sewer');
  if (u.trash) add('Trash collection', 'Trash Collection');
  if (u.range) add('Range / microwave (tenant-supplied)', 'Range/Microwave');
  if (u.refrigerator) add('Refrigerator (tenant-supplied)', 'Refrigerator');
  return { lines, total: lines.reduce((a, l) => a + l.amount, 0) };
}

const Toggle = ({ on, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`py-2.5 px-3 rounded-lg text-sm font-semibold transition-all text-left border
      ${on ? 'text-white border-transparent shadow-md' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'}`}
    style={on ? { backgroundColor: BRAND } : undefined}
  >
    {children}
  </button>
);

const FuelRow = ({ label, value, fuels, onChange }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
    <Toggle on={value !== 'landlord'} onClick={() => onChange(value === 'landlord' ? 'Electric' : 'landlord')}>
      {label}
    </Toggle>
    {value !== 'landlord' && (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sm:ml-auto px-3 py-2 text-sm border-2 border-slate-200 rounded-lg bg-white outline-none focus:border-slate-400"
      >
        {fuels.map(f => <option key={f} value={f}>{f.replace(' - ', ' ')}</option>)}
      </select>
    )}
  </div>
);

export default function SarasotaCalculatorPage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [zip, setZip] = useState('');
  const [areaData, setAreaData] = useState(null);

  const [householdSize, setHouseholdSize] = useState(1);
  const [annualIncome, setAnnualIncome] = useState('');

  const [dependents, setDependents] = useState(0);
  const [isElderlyDisabled, setIsElderlyDisabled] = useState(false);
  const [childcare, setChildcare] = useState('');
  const [medicalExp, setMedicalExp] = useState('');
  const [disabilityExp, setDisabilityExp] = useState('');

  const [voucherSize, setVoucherSize] = useState(1);
  const [unitBedrooms, setUnitBedrooms] = useState(null); // null = not sure yet
  const [unitType, setUnitType] = useState(DEFAULT_UNIT_TYPE);
  const [utils, setUtils] = useState(DEFAULT_UTILS);
  const [utilsIncluded, setUtilsIncluded] = useState(false);
  const activeUtils = utilsIncluded ? ALL_INCLUDED_UTILS : utils;
  const bedrooms = sizeUsed(voucherSize, unitBedrooms);

  const [result, setResult] = useState(null);

  const topRef = useRef(null);
  useEffect(() => { topRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [step]);

  const county = useMemo(() => (zip ? countyForZip(zip) : 'Sarasota County'), [zip]);
  const inArea = (z) => Object.prototype.hasOwnProperty.call(SARASOTA.paymentStandards.byZip, z);

  // Live UA preview on the details step
  const uaPreview = useMemo(() => computeUA(county, unitType, bedrooms, activeUtils), [county, unitType, bedrooms, activeUtils]);

  /* ── Step 0: ZIP ── */
  const lookupZip = async () => {
    if (!/^\d{5}$/.test(zip)) { setError('Please enter a valid 5-digit ZIP code.'); return; }
    if (!inArea(zip)) {
      setError(`ZIP ${zip} is outside the area Sarasota Housing Authority serves. Use the general calculator for other areas.`);
      return;
    }
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/calculator/lookup?zip=${zip}`);
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'ZIP code not found. Please check and try again.'); return; }
      setAreaData(data);
      trackCalculatorStart(zip);
      setStep(1);
    } catch (e) {
      setError('Unable to connect. Please try again.');
    } finally { setLoading(false); }
  };

  /* ── Step 1 → 2 ── */
  const checkEligibility = () => {
    const income = parseFloat(annualIncome.replace(/[,$\s]/g, '')) || 0;
    if (income <= 0) { setError('Please enter your annual household income.'); return; }
    setError(null);
    const size = Math.min(Math.max(householdSize, 1), 8);
    const limits = areaData.incomeLimits;
    const eligible = limits.veryLow && income <= limits.veryLow[size];
    if (!eligible) {
      setResult({ notEligible: true, limit: limits.veryLow?.[size] });
      trackCalculatorResult([], 0);
      setStep(3);
      return;
    }
    setVoucherSize(defaultVoucherSize(size));
    setStep(2);
  };

  /* ── Step 2 → 3 ── */
  const calculate = () => {
    const grossAnnual = parseFloat(annualIncome.replace(/[,$\s]/g, '')) || 0;
    const childcareAmt = parseFloat(childcare.replace(/[,$\s]/g, '')) || 0;
    const medicalAmt = parseFloat(medicalExp.replace(/[,$\s]/g, '')) || 0;
    const disabilityAmt = parseFloat(disabilityExp.replace(/[,$\s]/g, '')) || 0;

    let totalDeductions = dependents * DEDUCTIONS.dependentDeduction;
    if (isElderlyDisabled) totalDeductions += DEDUCTIONS.elderlyDisabledDeduction;
    totalDeductions += childcareAmt;
    if (isElderlyDisabled && medicalAmt > 0) {
      totalDeductions += Math.max(0, medicalAmt - grossAnnual * DEDUCTIONS.medicalThresholdPct);
    }
    totalDeductions += disabilityAmt;

    const adjustedAnnual = Math.max(0, grossAnnual - totalDeductions);
    const monthlyAdjusted = adjustedAnnual / 12;
    const monthlyGross = grossAnnual / 12;

    const ttp = Math.round(Math.max(
      monthlyAdjusted * DEDUCTIONS.ttpPctAdjusted,
      monthlyGross * DEDUCTIONS.ttpPctGross,
      SARASOTA.minimumRent
    ));

    const ua = computeUA(county, unitType, bedrooms, activeUtils);
    const rentToOwner = Math.max(0, ttp - ua.total);
    const reimbursement = Math.max(0, ua.total - ttp);

    const psRow = SARASOTA.paymentStandards.byZip[zip];
    const paymentStandard = psRow && bedrooms <= 4 ? psRow[bedrooms] : null;
    const moveInCap = Math.round(monthlyAdjusted * DEDUCTIONS.moveInShareCapPct);
    const headroom = Math.max(0, moveInCap - ttp);
    const rentAtStandard = paymentStandard != null ? Math.max(0, paymentStandard - ua.total) : null;
    const maxRentToOwner = paymentStandard != null ? rentAtStandard + headroom : null;

    const r = {
      notEligible: false,
      grossAnnual, totalDeductions, adjustedAnnual,
      monthlyAdjusted: Math.round(monthlyAdjusted),
      monthlyGross: Math.round(monthlyGross),
      ttp, ua, rentToOwner, reimbursement,
      paymentStandard, rentAtStandard, maxRentToOwner, moveInCap,
      county, unitType, bedrooms, voucherSize, unitBedrooms, utilsIncluded,
    };
    setResult(r);
    trackCalculatorResult(['Section 8 / Housing Choice Voucher'], ttp);
    setStep(3);

    try {
      fetch(`${API_BASE}/api/calculator/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zip, state: areaData?.state, fmrArea: areaData?.fmrArea,
          householdSize, incomeBracket: incomeBracket(grossAnnual),
          programsEligible: ['section8'], estimatedTtp: ttp,
          referrer: window.location.href,
        }),
      }).catch(() => {});
    } catch (e) {}
  };

  const reset = () => {
    setStep(0); setZip(''); setAreaData(null); setAnnualIncome(''); setHouseholdSize(1);
    setDependents(0); setIsElderlyDisabled(false); setChildcare(''); setMedicalExp(''); setDisabilityExp('');
    setVoucherSize(1); setUnitBedrooms(null); setUnitType(DEFAULT_UNIT_TYPE); setUtils(DEFAULT_UTILS); setUtilsIncluded(false);
    setResult(null); setError(null);
  };

  const primaryBtn = 'w-full py-3.5 text-white font-semibold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg';
  const input = 'w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-slate-400 outline-none transition-all placeholder:text-slate-300';

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-gray-50 to-slate-100">
      <div ref={topRef} />
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">

            {/* Heading */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-sm text-slate-700 mb-5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND }} />
                Preview for {SARASOTA.name} • {areaData?.fiscalYear || 'FY2026'} HUD income limits • 2026 SHA utility allowances
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
                Housing Choice Voucher Rent Estimator
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-1">
                See whether you may qualify for a voucher through {SARASOTA.name}, and what you would likely pay the landlord each month.
              </p>
              <p className="text-sm text-gray-400">
                Sarasota and Manatee counties. Uses SHA's published utility allowances and payment standards.
              </p>
            </div>

            <div className="max-w-2xl mx-auto">

              {/* Step indicators */}
              <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-6">
                {['Location', 'Household', 'Your Unit', 'Results'].map((label, i) => (
                  <React.Fragment key={label}>
                    {i > 0 && <div className={`h-px w-5 sm:w-8 ${i <= step ? 'bg-slate-400' : 'bg-slate-200'}`} />}
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
                          ${i < step ? 'bg-emerald-500 text-white' : i === step ? 'text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-200'}`}
                        style={i === step ? { backgroundColor: BRAND } : undefined}
                      >
                        {i < step ? '✓' : i + 1}
                      </div>
                      <span className={`text-xs font-medium hidden sm:inline ${i <= step ? 'text-slate-700' : 'text-slate-400'}`}>{label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
                {error && (
                  <div className="bg-red-50 border-b border-red-100 px-6 py-3 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                    <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                  </div>
                )}

                <div className="p-6 sm:p-8">

                  {/* ── STEP 0: ZIP ── */}
                  {step === 0 && (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-xl font-bold text-slate-800 mb-1">Where do you plan to live?</h2>
                        <p className="text-sm text-slate-500">Enter the ZIP code where you would use your voucher. SHA's payment standards are set by ZIP code.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">ZIP Code</label>
                        <input
                          type="text" inputMode="numeric" maxLength={5}
                          className={`${input} text-lg`}
                          placeholder="e.g. 34236"
                          value={zip}
                          onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                          onKeyDown={(e) => e.key === 'Enter' && lookupZip()}
                          autoFocus
                        />
                      </div>
                      <button onClick={lookupZip} disabled={zip.length !== 5 || loading} className={primaryBtn} style={{ backgroundColor: BRAND }}>
                        {loading ? (<span className="flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Looking up...</span>) : 'Look Up My Area →'}
                      </button>
                      <p className="text-xs text-slate-400 text-center">
                        Outside Sarasota and Manatee counties? <Link to="/calculator" className="underline hover:text-slate-600">Use the nationwide calculator</Link>.
                      </p>
                    </div>
                  )}

                  {/* ── STEP 1: Household ── */}
                  {step === 1 && areaData && (
                    <div className="space-y-6">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-emerald-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">ZIP {zip} • {county}</p>
                            <p className="text-xs text-emerald-600">{areaData.fmrArea}</p>
                            <p className="text-xs text-emerald-500 mt-1">Area Median Income: {fmt(areaData.medianFamilyIncome)} (family of 4) • {areaData.fiscalYear}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h2 className="text-xl font-bold text-slate-800 mb-1">Household Information</h2>
                        <p className="text-sm text-slate-500">A voucher requires household income at or below the Very Low Income limit (50% of area median).</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Household Size</label>
                        <p className="text-xs text-slate-400 mb-2">Everyone who will live in the unit, including children</p>
                        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                          {[1,2,3,4,5,6,7,8].map(n => (
                            <Toggle key={n} on={householdSize === n} onClick={() => setHouseholdSize(n)}><span className="block text-center">{n}</span></Toggle>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Total Annual Gross Household Income</label>
                        <p className="text-xs text-slate-400 mb-2">Before taxes. Combine wages, benefits, Social Security and other income from all household members.</p>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                          <input
                            type="text" inputMode="numeric"
                            className={`${input} pl-8 text-lg`}
                            placeholder="e.g. 28,000"
                            value={annualIncome}
                            onChange={(e) => { const raw = e.target.value.replace(/[^0-9]/g, ''); setAnnualIncome(raw ? parseInt(raw).toLocaleString() : ''); }}
                            onKeyDown={(e) => e.key === 'Enter' && checkEligibility()}
                          />
                        </div>
                      </div>

                      {areaData.incomeLimits?.veryLow && (
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Income limits for a household of {householdSize}</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between gap-2"><span className="text-slate-500">Extremely Low <span className="text-slate-400">(30% AMI)</span></span><span className="font-semibold text-slate-700">{fmt(areaData.incomeLimits.extremelyLow[householdSize])}</span></div>
                            <div className="flex justify-between gap-2"><span className="text-slate-700 font-medium">Very Low <span className="text-slate-400 font-normal">(50% AMI) — voucher limit</span></span><span className="font-bold text-slate-800">{fmt(areaData.incomeLimits.veryLow[householdSize])}</span></div>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <button onClick={() => setStep(0)} className="px-4 py-3 text-slate-500 font-medium rounded-xl hover:bg-slate-50 transition-all">← Back</button>
                        <button onClick={checkEligibility} disabled={!annualIncome} className={`flex-1 ${primaryBtn}`} style={{ backgroundColor: BRAND }}>Check Eligibility →</button>
                      </div>
                    </div>
                  )}

                  {/* ── STEP 2: Deductions + unit + utilities ── */}
                  {step === 2 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                        <span className="text-emerald-500 text-lg">✓</span>
                        <div>
                          <p className="text-sm font-semibold text-emerald-800">Your income is within the voucher limit</p>
                          <p className="text-xs text-emerald-600">Household of {householdSize} in {county}</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-bold text-slate-800 mb-1">About your household</h3>
                        <p className="text-sm text-slate-500">These set the deductions HUD allows before your share of rent is figured.</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Dependents under 18</label>
                        <p className="text-xs text-slate-400 mb-2">Each one reduces adjusted income by $480 per year</p>
                        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                          {[0,1,2,3,4,5,6,7].map(n => (
                            <Toggle key={n} on={dependents === n} onClick={() => setDependents(n)}><span className="block text-center">{n}</span></Toggle>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Is the head of household or spouse 62 or older, or disabled?</label>
                        <p className="text-xs text-slate-400 mb-2">Adds a $525 deduction and allows a medical expense deduction</p>
                        <div className="grid grid-cols-2 gap-3">
                          <Toggle on={isElderlyDisabled} onClick={() => setIsElderlyDisabled(true)}><span className="block text-center">Yes</span></Toggle>
                          <Toggle on={!isElderlyDisabled} onClick={() => setIsElderlyDisabled(false)}><span className="block text-center">No</span></Toggle>
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Annual childcare expenses</label>
                          <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            <input type="text" inputMode="numeric" className={`${input} pl-8`} placeholder="0" value={childcare} onChange={(e) => setChildcare(e.target.value.replace(/[^0-9]/g, ''))} /></div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Annual disability assistance expenses</label>
                          <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            <input type="text" inputMode="numeric" className={`${input} pl-8`} placeholder="0" value={disabilityExp} onChange={(e) => setDisabilityExp(e.target.value.replace(/[^0-9]/g, ''))} /></div>
                        </div>
                        {isElderlyDisabled && (
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Annual unreimbursed medical expenses</label>
                            <p className="text-xs text-slate-400 mb-2">The amount above 3% of gross income is deducted</p>
                            <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                              <input type="text" inputMode="numeric" className={`${input} pl-8`} placeholder="0" value={medicalExp} onChange={(e) => setMedicalExp(e.target.value.replace(/[^0-9]/g, ''))} /></div>
                          </div>
                        )}
                      </div>

                      <hr className="border-slate-100" />

                      <div>
                        <h3 className="text-lg font-bold text-slate-800 mb-1">About the unit</h3>
                        <p className="text-sm text-slate-500">SHA's payment standard and utility allowance depend on your voucher size, the building type, and which utilities you pay yourself.</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Voucher size (bedrooms on your voucher)</label>
                        <p className="text-xs text-slate-400 mb-2">
                          Set by SHA from your household, not by the unit you pick: a single person gets 1 bedroom, a couple shares a bedroom, and children share a room until age 12.
                          Suggested for a household of {householdSize}: {defaultVoucherSize(householdSize)} BR.
                        </p>
                        <div className="grid grid-cols-5 gap-2">
                          {VOUCHER_SIZES.map(n => (
                            <Toggle key={n} on={voucherSize === n} onClick={() => setVoucherSize(n)}><span className="block text-center">{n} BR</span></Toggle>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Bedrooms in the unit you plan to rent</label>
                        <p className="text-xs text-slate-400 mb-2">If the unit has more bedrooms than your voucher, SHA still uses your voucher size. If it has fewer, SHA uses the unit's size.</p>
                        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                          <Toggle on={unitBedrooms === null} onClick={() => setUnitBedrooms(null)}><span className="block text-center">Not sure</span></Toggle>
                          {SARASOTA.ua.bedrooms.map(n => (
                            <Toggle key={n} on={unitBedrooms === n} onClick={() => setUnitBedrooms(n)}><span className="block text-center">{n === 0 ? 'Studio' : `${n} BR`}</span></Toggle>
                          ))}
                        </div>
                        {unitBedrooms != null && unitBedrooms !== voucherSize && (
                          <p className="mt-2 px-1 text-xs text-slate-500">
                            <Info className="h-3.5 w-3.5 inline mr-1 text-slate-400" />
                            {unitBedrooms > voucherSize
                              ? `Your voucher is for ${voucherSize} bedroom${voucherSize === 1 ? '' : 's'}, so SHA figures the payment standard and utility allowance on the ${voucherSize}-bedroom schedule even though the unit has ${unitBedrooms}.`
                              : `The unit is smaller than your voucher, so SHA figures the payment standard and utility allowance on the ${unitBedrooms === 0 ? 'studio' : `${unitBedrooms}-bedroom`} schedule.`}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Type of building</label>
                        <p className="text-xs text-slate-400 mb-2">SHA estimates with the single-family schedule unless you know the building type.</p>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {UNIT_TYPE_ORDER.map(t => (
                            <Toggle key={t} on={unitType === t} onClick={() => setUnitType(t)}>{UNIT_TYPE_LABELS[t]}</Toggle>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Utilities</label>
                        <p className="text-xs text-slate-400 mb-2">If the landlord pays all utilities, there is no utility allowance and you pay SHA's full tenant share to the landlord.</p>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <Toggle on={!utilsIncluded} onClick={() => setUtilsIncluded(false)}><span className="block text-center">I pay some utilities</span></Toggle>
                          <Toggle on={utilsIncluded} onClick={() => setUtilsIncluded(true)}><span className="block text-center">All utilities included in rent</span></Toggle>
                        </div>
                        {!utilsIncluded && (<>
                        <p className="text-xs text-slate-400 mb-2">Tap each utility you will pay yourself. Most Sarasota units are all-electric with the tenant paying the electric bill.</p>
                        <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl px-3">
                          <div className="py-2">
                            <Toggle on={utils.electric} onClick={() => setUtils({ ...utils, electric: !utils.electric })}>Electricity (lights, A/C, appliances)</Toggle>
                          </div>
                          <FuelRow label="Heating" value={utils.heating} fuels={HEAT_FUELS} onChange={(v) => setUtils({ ...utils, heating: v })} />
                          <FuelRow label="Cooking" value={utils.cooking} fuels={COOK_FUELS} onChange={(v) => setUtils({ ...utils, cooking: v })} />
                          <FuelRow label="Water heating" value={utils.waterHeating} fuels={HEAT_FUELS} onChange={(v) => setUtils({ ...utils, waterHeating: v })} />
                          <div className="py-2 grid grid-cols-3 gap-2">
                            <Toggle on={utils.water} onClick={() => setUtils({ ...utils, water: !utils.water })}>Water</Toggle>
                            <Toggle on={utils.sewer} onClick={() => setUtils({ ...utils, sewer: !utils.sewer })}>Sewer</Toggle>
                            <Toggle on={utils.trash} onClick={() => setUtils({ ...utils, trash: !utils.trash })}>Trash</Toggle>
                          </div>
                          <div className="py-2 grid grid-cols-2 gap-2">
                            <Toggle on={utils.refrigerator} onClick={() => setUtils({ ...utils, refrigerator: !utils.refrigerator })}>I supply the refrigerator</Toggle>
                            <Toggle on={utils.range} onClick={() => setUtils({ ...utils, range: !utils.range })}>I supply the range</Toggle>
                          </div>
                        </div>
                        </>)}
                        <div className="mt-2 flex items-center justify-between text-sm px-1">
                          <span className="text-slate-500 flex items-center gap-1"><Zap className="h-4 w-4 text-amber-500" /> Utility allowance ({bedrooms === 0 ? 'studio' : `${bedrooms} BR`}, {UNIT_TYPE_LABELS[unitType].split(' (')[0].toLowerCase()})</span>
                          <span className="font-bold text-slate-800">{fmt(uaPreview.total)}/mo</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button onClick={() => setStep(1)} className="px-4 py-3 text-slate-500 font-medium rounded-xl hover:bg-slate-50 transition-all">← Back</button>
                        <button onClick={calculate} className={`flex-1 ${primaryBtn}`} style={{ backgroundColor: BRAND }}>Estimate My Rent →</button>
                      </div>
                    </div>
                  )}

                  {/* ── STEP 3: Results ── */}
                  {step === 3 && result && (
                    <div className="space-y-6">
                      {result.notEligible ? (
                        <div className="text-center py-8">
                          <div className="flex justify-center mb-4"><Frown className="h-12 w-12 text-slate-400" /></div>
                          <h2 className="text-xl font-bold text-slate-800 mb-2">Based on the information provided</h2>
                          <p className="text-slate-500 max-w-md mx-auto mb-6">
                            Your household income is above the Very Low Income limit of {fmt(result.limit)} for a household of {householdSize}, which is the limit for a Housing Choice Voucher.
                          </p>
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6">
                            <p className="text-sm text-amber-800">
                              <strong>Keep in mind:</strong> income limits change every spring, and only verified income counts. Call {SARASOTA.name} at {SARASOTA.phone} if your situation has changed.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Hero */}
                          <div className="rounded-2xl p-5 sm:p-8 text-center text-white shadow-xl" style={{ background: `linear-gradient(135deg, ${BRAND}, #0d3352)` }}>
                            <p className="text-white/80 text-sm font-medium mb-1">Estimated rent you pay the landlord</p>
                            <p className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-1">{fmtMonthly(result.rentToOwner)}</p>
                            <p className="text-white/70 text-sm">
                              per month with a {result.voucherSize}-bedroom voucher in ZIP {zip}
                              {result.unitBedrooms != null && result.unitBedrooms !== result.voucherSize ? ` (${result.unitBedrooms === 0 ? 'studio' : `${result.unitBedrooms}-bedroom`} unit, figured on the ${result.bedrooms === 0 ? 'studio' : `${result.bedrooms}-bedroom`} schedule)` : ''}
                            </p>
                            <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-3 gap-2 text-xs sm:text-sm">
                              <div><p className="text-white/60">Your total share</p><p className="font-bold">{fmt(result.ttp)}</p></div>
                              <div><p className="text-white/60">Utility allowance</p><p className="font-bold">− {fmt(result.ua.total)}</p></div>
                              <div><p className="text-white/60">Rent to landlord</p><p className="font-bold">{fmt(result.rentToOwner)}</p></div>
                            </div>
                          </div>

                          {result.reimbursement > 0 && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                              <p className="text-sm text-emerald-800">
                                <strong>Utility reimbursement:</strong> your utility allowance is larger than your share, so SHA would pay the full rent and send you about {fmt(result.reimbursement)} a month toward utility bills.
                              </p>
                            </div>
                          )}

                          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                            <p className="text-sm text-slate-700">
                              <Info className="h-4 w-4 inline mr-1 text-slate-400" />
                              <strong>How this works.</strong> Your total share of housing costs is {fmt(result.ttp)} a month (30% of adjusted income, or the $50 minimum).{' '}
                              {result.utilsIncluded
                                ? 'Because the landlord pays all utilities, there is no utility allowance and your full share goes to the landlord.'
                                : `Because you pay utilities yourself, SHA subtracts a ${fmt(result.ua.total)} utility allowance (${UNIT_TYPE_LABELS[result.unitType].split(' (')[0].toLowerCase()} schedule, ${result.bedrooms === 0 ? 'studio' : `${result.bedrooms} BR`}), and the rest is what you pay the landlord.`}
                            </p>
                          </div>

                          {/* Payment standard */}
                          {result.paymentStandard != null ? (
                            <div className="border border-slate-100 rounded-xl divide-y divide-slate-100 overflow-hidden">
                              <div className="px-4 py-3 bg-slate-50"><p className="text-xs font-bold text-slate-500 uppercase tracking-wide">What rent you can look for in ZIP {zip}</p></div>
                              <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm">
                                <span className="text-slate-500">SHA payment standard ({result.bedrooms === 0 ? 'studio' : `${result.bedrooms} BR`}, rent + utilities)</span>
                                <span className="font-semibold text-slate-700 shrink-0">{fmt(result.paymentStandard)}</span>
                              </div>
                              <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm">
                                <span className="text-slate-500">Rent to landlord at or below this, and you pay exactly {fmt(result.rentToOwner)}</span>
                                <span className="font-semibold text-slate-700 shrink-0">{fmt(result.rentAtStandard)}</span>
                              </div>
                              <div className="px-4 py-3 flex justify-between items-baseline gap-3 text-sm bg-slate-50">
                                <span className="text-slate-500">Highest rent to landlord you could lease at move-in (your share capped at 40% of adjusted income)</span>
                                <span className="font-semibold text-slate-700 shrink-0">{fmt(result.maxRentToOwner)}</span>
                              </div>
                              <div className="px-4 py-2.5 text-xs text-slate-400">
                                Above {fmt(result.rentAtStandard)} you pay the extra yourself. SHA must also find the rent reasonable for the unit.
                              </div>
                            </div>
                          ) : (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-500">
                              SHA publishes payment standards for studio through 4-bedroom units. Ask SHA for the 5-bedroom standard in your ZIP.
                            </div>
                          )}

                          {/* Breakdown */}
                          <div className="border border-slate-100 rounded-xl divide-y divide-slate-100 overflow-hidden">
                            <div className="px-4 py-3 bg-slate-50"><p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Calculation</p></div>
                            <div className="px-4 py-3 flex justify-between text-sm"><span className="text-slate-500">Gross annual income</span><span className="font-semibold text-slate-700">{fmt(result.grossAnnual)}</span></div>
                            {result.totalDeductions > 0 && (
                              <div className="px-4 py-3 flex justify-between text-sm"><span className="text-slate-500">Deductions</span><span className="font-semibold text-red-600">− {fmt(result.totalDeductions)}</span></div>
                            )}
                            <div className="px-4 py-3 flex justify-between text-sm"><span className="text-slate-500">Adjusted annual income</span><span className="font-semibold text-slate-700">{fmt(result.adjustedAnnual)}</span></div>
                            <div className="px-4 py-3 flex justify-between text-sm bg-slate-50"><span className="text-slate-500">30% of adjusted monthly income</span><span className="font-semibold text-slate-700">{fmt(result.monthlyAdjusted * 0.3)}</span></div>
                            <div className="px-4 py-3 flex justify-between text-sm"><span className="text-slate-500">10% of gross monthly income</span><span className="font-semibold text-slate-700">{fmt(result.monthlyGross * 0.1)}</span></div>
                            <div className="px-4 py-3 flex justify-between text-sm bg-slate-50"><span className="text-slate-500">SHA minimum rent</span><span className="font-semibold text-slate-700">{fmt(SARASOTA.minimumRent)}</span></div>
                            <div className="px-4 py-3 flex justify-between text-sm"><span className="text-slate-700 font-semibold">Total Tenant Payment (highest of the three)</span><span className="font-bold text-slate-800">{fmt(result.ttp)}</span></div>
                            {result.ua.lines.map(l => (
                              <div key={l.label} className="px-4 py-2 flex justify-between text-sm bg-amber-50/40"><span className="text-slate-500 pl-3">Utility allowance: {l.label}</span><span className="text-slate-600">− {fmt(l.amount)}</span></div>
                            ))}
                            <div className="px-4 py-3 flex justify-between text-sm" style={{ backgroundColor: `${BRAND}14` }}><span className="font-bold text-slate-800">Rent to landlord</span><span className="font-extrabold text-slate-900">{fmt(result.rentToOwner)}/mo</span></div>
                          </div>

                          {/* Apply */}
                          <div className="space-y-2">
                            <p className="text-sm font-semibold text-slate-700">Next steps with {SARASOTA.name}</p>
                            <a href={SARASOTA.portals.applicant} target="_blank" rel="noopener noreferrer" className="block w-full py-3 text-center text-white font-semibold rounded-xl transition-all shadow-lg" style={{ backgroundColor: BRAND }}>
                              Apply or check the waiting list <ExternalLink className="h-4 w-4 inline ml-1" />
                            </a>
                            <a href={`${SARASOTA.website}/section8.aspx`} target="_blank" rel="noopener noreferrer" className="block w-full py-3 text-center bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-all">
                              Housing Choice Voucher program details
                            </a>
                            <p className="text-xs text-slate-400 text-center">Questions? Call {SARASOTA.phone}, Monday–Thursday 8–5.</p>
                          </div>
                        </>
                      )}

                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                        <p className="text-xs text-slate-500 leading-relaxed">
                          <AlertTriangle className="h-3.5 w-3.5 text-slate-400 inline mr-1" /><strong>Estimate only.</strong> Based on {areaData?.fiscalYear} HUD income limits, SHA's utility allowance schedule for {result.county || county} ({SARASOTA.ua.effective}{result.unitType ? `, ${UNIT_TYPE_LABELS[result.unitType].split(' (')[0].toLowerCase()}` : ''}), and SHA's payment standards effective {SARASOTA.paymentStandards.effective}. The payment standard and allowance use the lower of your voucher size and the unit's bedrooms. Your actual share is set by SHA after verifying income, deductions, voucher size and the unit's utilities. Nothing here guarantees a voucher or a particular rent.
                        </p>
                      </div>

                      <div className="flex gap-3">
                        {!result.notEligible && (
                          <button onClick={() => setStep(2)} className="px-4 py-3 text-slate-500 font-medium rounded-xl hover:bg-slate-50 transition-all">← Edit Details</button>
                        )}
                        <button onClick={reset} className="flex-1 py-3 bg-slate-100 text-slate-600 font-semibold rounded-xl hover:bg-slate-200 transition-all">Start Over</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <p className="mt-6 text-center text-xs text-slate-400">
                Preview build by HousingAI for {SARASOTA.name}. Sources:{' '}
                <a className="underline" href={SARASOTA.ua.sources['Sarasota County']} target="_blank" rel="noopener noreferrer">utility allowances</a>,{' '}
                <a className="underline" href={SARASOTA.paymentStandards.source} target="_blank" rel="noopener noreferrer">payment standards</a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
